import { Quaternion } from "@babylonjs/core/Maths/math.vector";
import { quaternionToAxisAngle } from "../../core/utils/MathUtils";
import * as basket from "../../eaiws/basket";
import { fromBlob, isNotNullOrEmpty, isNullOrEmpty, trimRight } from "../../utils/string";
/** @internal */
export class ObxReader {
    get isAvailable() {
        return (this.mDoc != null);
    }
    constructor() {
        this.mDoc = null;
        this.mItemRoot = null;
        this.mLanguages = ["en"];
        this.mElementData = new Map();
    }
    getItems(pItemIds) {
        const tItems = new Array();
        for (const tItemId of pItemIds) {
            const tData = this.getElementData(tItemId);
            if (tData != null) {
                const tItem = new basket.BasketItem();
                tItem.itemId = tData.itemId;
                tItem.itemType = tData.itemType;
                tItem.label = tData.label;
                if (tData.article != null) {
                    tItem.mainArticleId = tData.article.mainArticleId;
                    tItem.subArticleIds = tData.article.subArticleIds;
                    tItem.geometryId = tData.article.geometryId;
                }
                else
                    tItem.mainArticleId = "00000000-0000-0000-0000-000000000000";
                tItems.push(tItem);
            }
        }
        return (tItems);
    }
    getAllItems() {
        return (this.getItems(this.getAllItemIds()));
    }
    getItemProperties(pItemIds) {
        const tItemProps = new Array();
        for (const tItemId of pItemIds) {
            const tData = this.getElementData(tItemId);
            if (tData != null) {
                const tProps = new basket.ItemProperties();
                tProps.itemId = tData.itemId;
                tProps.itemType = tData.itemType;
                tProps.label = tData.label;
                tProps.visible = true;
                //TODO: tProps.setArticleId
                tProps.composableGeometry = tData.composableGeometry;
                if (tProps.itemType === "Folder") {
                    tProps.folder = new basket.FolderProperties();
                    tProps.folder.name = tProps.label;
                }
                else if (tData.article != null) {
                    tProps.article = new basket.ArticleProperties();
                    tProps.article.catalogId = "";
                    tProps.article.manufacturerId = tData.article.manufacturerId;
                    tProps.article.manufacturerName = tData.article.manufacturer;
                    tProps.article.seriesId = tData.article.seriesId;
                    tProps.article.seriesName = tData.article.series;
                    tProps.article.baseArticleNumber = tData.article.baseArtNum;
                    tProps.article.finalArticleNumber = tData.article.finalArtNum;
                    tProps.article.variantCode = tData.article.varCode;
                    tProps.article.ofmlVariantCode = tData.article.ofmlVarCode;
                    tProps.article.useFinalArticleNumber = tData.article.useFinalArtNum;
                    tProps.article.shortText = tData.article.shortText;
                    tProps.article.longText = tData.article.longText;
                    tProps.article.featureText = tData.article.featureText;
                    tProps.article.currency = tData.article.currency;
                    tProps.article.purchasePrice = tData.article.pdPurchasePrice;
                    tProps.article.salesPrice = tData.article.pdSalesPrice;
                    tProps.article.quantity = tData.article.quantity;
                    tProps.article.alternativePosition = tData.article.alternativePosition;
                    tProps.article.excludedCalculations = undefined; //TODO
                    tProps.article.inconsistencyFlag = tData.article.inconsistencyFlag;
                    tProps.article.inconsistencyReason = tData.article.inconsistencyReason;
                    tProps.article.packagingInfo = undefined; //TODO
                    tProps.article.ofmlUpdateState = "Unknown";
                    tProps.article.geometryId = tData.article.geometryId;
                }
                tItemProps.push(tProps);
            }
        }
        return (tItemProps);
    }
    getAllItemProperties() {
        return (this.getItemProperties(this.getAllItemIds()));
    }
    /**
     * Set the languages used for article text. Default is "en".
     */
    setLanguages(pLanguages) {
        if (pLanguages != null && pLanguages.length > 0) {
            this.mLanguages = pLanguages;
            if (this.isAvailable) {
                this.mElementData.clear();
            }
        }
    }
    //// internal //////////////////////////////////////////////////////////////////////////////
    /** @internal **/
    _loadObx(pBlob) {
        this._clear();
        const tPromise = fromBlob(pBlob).then((pObxString) => {
            const tDoc = this.parseXml(pObxString);
            if (this.readObxDocument(tDoc)) {
                return (Promise.resolve());
            }
            else
                return (Promise.reject(new Error("Invalid file.")));
        }).catch((pError) => {
            return (Promise.reject(new Error("Failed to read obx: " + pError)));
        });
        return (tPromise);
    }
    /** @internal **/
    _clear() {
        this.mDoc = null;
        this.mItemRoot = null;
        this.mElementData.clear();
    }
    getChildElement(pParent, pTag) {
        if (pParent == null)
            return (null);
        for (let i = 0; i < pParent.children.length; ++i) {
            const tChild = pParent.children.item(i);
            if (tChild != null && tChild.tagName === pTag)
                return (tChild);
        }
        return (null);
    }
    getChildElements(pParent, pTag) {
        const tElements = new Array();
        if (pParent != null) {
            for (let i = 0; i < pParent.children.length; ++i) {
                const tChild = pParent.children.item(i);
                if (tChild != null && tChild.tagName === pTag)
                    tElements.push(tChild);
            }
        }
        return (tElements);
    }
    queryChildElement(pParent, pSelector) {
        if (pParent == null)
            return (null);
        const tElements = pParent.querySelectorAll(pSelector);
        for (let i = 0; i < tElements.length; ++i) {
            const tElement = tElements.item(i);
            if (tElement.parentNode === pParent)
                return (tElement);
        }
        return (null);
    }
    parseXml(pXmlString) {
        const tParser = new DOMParser();
        const tDoc = tParser.parseFromString(pXmlString, "application/xml");
        return (tDoc);
    }
    readObxDocument(pDoc) {
        if (pDoc == null || pDoc.documentElement == null)
            return (false);
        if (pDoc.documentElement.tagName !== "cutBuffer")
            return (false);
        this.mDoc = pDoc.documentElement;
        this.mItemRoot = this.getChildElement(this.mDoc, "items");
        return (true);
    }
    getElementData(pBasketId) {
        if (isNullOrEmpty(pBasketId) || this.mItemRoot == null)
            return (null);
        let tData = this.mElementData.get(pBasketId);
        if (tData !== undefined)
            return (tData);
        tData = null;
        const tElement = this.mItemRoot.querySelector("[basketId='" + pBasketId + "']");
        if (tElement != null) {
            if (tElement.tagName === "bskArticle" || tElement.tagName === "usrArticle") {
                tData = this.getArticleElementData(pBasketId, tElement);
            }
            else if (tElement.tagName === "topFolder" || tElement.tagName === "bskFolder") {
                tData = this.getFolderElementData(tElement);
            }
            if (tData != null) {
                tData.itemId = pBasketId;
                tData.composableGeometry = this.getComposableGeometryData(tElement);
            }
        }
        this.mElementData.set(pBasketId, tData);
        return (tData);
    }
    getFolderElementData(pElement) {
        if (pElement == null)
            return (null);
        const tData = {};
        tData.itemType = "Folder";
        //label
        const tElement = this.getChildElement(pElement, "label");
        if (tElement != null) {
            tData.label = pElement.textContent;
        }
        return (tData);
    }
    getArticleElementData(pBasketId, pElement) {
        if (pElement == null)
            return (null);
        let tElement;
        const tData = {};
        const tType = pElement.getAttribute("itemType");
        switch (tType) {
            case "BasketAggregate":
                tData.itemType = "Aggregate";
                break;
            case "BasketPartialPlanning":
                tData.itemType = "PartialPlanning";
                break;
            case "UserArticle":
                tData.itemType = "UserArticle";
                break;
            default:
                tData.itemType = "Article";
        }
        const tAData = {};
        tData.article = tAData;
        //manufacturer
        tElement = this.getChildElement(pElement, "manufacturer");
        if (tElement != null) {
            tAData.manufacturerId = tElement.getAttribute("id");
            tAData.manufacturer = this.getLangText("name", tElement);
        }
        //series
        tElement = this.getChildElement(pElement, "series");
        if (tElement != null) {
            tAData.seriesId = tElement.getAttribute("id");
            tAData.series = this.getLangText("name", tElement);
        }
        //short text
        tElement = this.queryChildElement(pElement, "description[type='short']");
        if (tElement != null) {
            tAData.shortText = this.getLangText("text", tElement);
            tData.label = tAData.shortText;
        }
        //long text
        tElement = this.queryChildElement(pElement, "description[type='long']");
        if (tElement != null) {
            tAData.longText = this.getLangText("text", tElement);
            if (tElement.getAttribute("default") === "1")
                tData.label = tAData.longText;
        }
        //feature text
        tElement = this.queryChildElement(pElement, "description[type='features']");
        if (tElement != null) {
            tAData.featureText = this.getLangText("text", tElement);
        }
        //base article number
        tElement = this.queryChildElement(pElement, "artNr[type='base']");
        if (tElement != null) {
            tAData.baseArtNum = tElement.textContent;
        }
        //final article number
        tElement = this.queryChildElement(pElement, "artNr[type='final']");
        if (tElement != null) {
            tAData.finalArtNum = tElement.textContent;
            tAData.useFinalArtNum = tElement.getAttribute("default") === "1";
        }
        //var code
        tElement = this.queryChildElement(pElement, "artNr[type='varcode']");
        if (tElement != null) {
            tAData.varCode = tElement.textContent;
        }
        //ofml var code
        tElement = this.queryChildElement(pElement, "artNr[type='ofmlvarcode']");
        if (tElement != null) {
            tAData.ofmlVarCode = tElement.textContent;
        }
        //quantity
        tAData.quantity = 1;
        tElement = this.getChildElement(pElement, "quantity");
        if (tElement != null) {
            tAData.quantity = parseFloat(tElement.getAttribute("count"));
        }
        //pd sales price
        tElement = this.queryChildElement(pElement, "itemPrice[type='sale'][pd='1']");
        if (tElement != null) {
            tAData.pdSalesPrice = parseFloat(tElement.getAttribute("value"));
            tAData.currency = tElement.getAttribute("currency");
        }
        //pd purchase price
        tElement = this.queryChildElement(pElement, "itemPrice[type='purchase'][pd='1']");
        if (tElement != null) {
            tAData.pdSalesPrice = parseFloat(tElement.getAttribute("value"));
            tAData.currency = tElement.getAttribute("currency");
        }
        //alternativePosition
        tElement = this.getChildElement(this.getChildElement(pElement, "exclOffers"), "exclOffer");
        tAData.alternativePosition = tElement != null;
        //inconsistency
        const tElements = this.getChildElements(this.getChildElement(pElement, "inconsistency"), "text");
        if (tElements.length > 0)
            tAData.inconsistencyReason = [];
        for (const tText of tElements) {
            tAData.inconsistencyReason.push(tText.textContent);
        }
        tAData.inconsistencyFlag = tAData.inconsistencyReason != null &&
            tAData.inconsistencyReason.length > 0;
        //geometry id (aggregated checksum has prioririty)
        tElement = this.queryChildElement(pElement, "geometryChecksum[aggr='1']");
        if (tElement == null)
            tElement = this.getChildElement(pElement, "geometryChecksum");
        if (tElement != null) {
            tAData.geometryId = tElement.getAttribute("value");
            //add transform checksum to id
            tElement = this.getChildElement(pElement, "geoTransformCksum");
            if (tElement != null && tAData.geometryId != null) {
                //checksum is base64 encoded
                const tBinGeomChks = atob(tAData.geometryId);
                const tBinTransChks = atob(tElement.getAttribute("value"));
                const tGeomId = btoa(tBinGeomChks + tBinTransChks);
                tAData.geometryId = trimRight(tGeomId, "="); //remove padding char
            }
        }
        //convert base64 string to url format
        if (!isNullOrEmpty(tAData.geometryId)) {
            tAData.geometryId = tAData.geometryId.replace(/\//g, "_");
            tAData.geometryId = tAData.geometryId.replace(/\+/g, "-");
        }
        //main article id
        tAData.mainArticleId = "00000000-0000-0000-0000-000000000000";
        tElement = this.getChildElement(pElement, "mainItem");
        if (tElement != null) {
            const tId = tElement.getAttribute("id");
            if (tId != null)
                tAData.mainArticleId = tId;
        }
        //sub article ids
        tAData.subArticleIds = this.getSubArticleIds(pBasketId);
        return (tData);
    }
    getComposableGeometryData(pElement) {
        if (pElement == null)
            return (null);
        const tGeomElement = this.getChildElement(pElement, "composableGeometry");
        if (tGeomElement == null)
            return (null);
        const tProps = new basket.ComposableGeometryProperties();
        tProps.enabled = tGeomElement.getAttribute("enabled") === "true";
        const tGeometry = tGeomElement.getAttribute("geometry");
        if (!isNullOrEmpty(tGeometry))
            tProps.geometry = tGeometry;
        const tPosElement = this.getChildElement(tGeomElement, "position");
        if (tPosElement != null) {
            tProps.position = new basket.Vector3();
            tProps.position.x = parseFloat(tPosElement.getAttribute("x"));
            tProps.position.y = parseFloat(tPosElement.getAttribute("y"));
            tProps.position.z = parseFloat(tPosElement.getAttribute("z"));
        }
        const tRotElement = this.getChildElement(tGeomElement, "rotation");
        if (tRotElement != null) {
            //rotation is stored as quaternion in obk
            const tQuat = new Quaternion(parseFloat(tRotElement.getAttribute("x")), parseFloat(tRotElement.getAttribute("y")), -parseFloat(tRotElement.getAttribute("z")), -parseFloat(tRotElement.getAttribute("w")));
            const tAxisAngle = quaternionToAxisAngle(tQuat);
            tProps.rotation = new basket.Rotation();
            tProps.rotation.axis = new basket.Vector3();
            tProps.rotation.axis.x = tAxisAngle.x;
            tProps.rotation.axis.y = tAxisAngle.y;
            tProps.rotation.axis.z = -tAxisAngle.z;
            tProps.rotation.angle = -tAxisAngle.w;
        }
        const tScaleElement = this.getChildElement(tGeomElement, "scale");
        if (tScaleElement != null) {
            tProps.scale = new basket.Vector3();
            tProps.scale.x = parseFloat(tScaleElement.getAttribute("x"));
            tProps.scale.y = parseFloat(tScaleElement.getAttribute("y"));
            tProps.scale.z = parseFloat(tScaleElement.getAttribute("z"));
        }
        return (tProps);
    }
    getLangText(pTagName, pElement) {
        if (pElement == null)
            return (null);
        for (const tLang of this.mLanguages) {
            const tText = this.queryChildElement(pElement, pTagName + "[lang=\"" + tLang + "\"]");
            if (tText != null)
                return (tText.textContent);
        }
        let tText = this.queryChildElement(pElement, pTagName + "[lang=\"\"]");
        if (tText != null)
            return (tText.textContent);
        //use first
        tText = this.getChildElement(pElement, pTagName);
        if (tText != null)
            return (tText.textContent);
        return (null);
    }
    getAllItemIds() {
        const tItemIds = new Array();
        if (this.mItemRoot == null)
            return (tItemIds);
        const tElements = this.mItemRoot.querySelectorAll("[basketId]");
        for (let i = 0; i < tElements.length; ++i) {
            const tElement = tElements.item(i);
            const tBasketId = tElement.getAttribute("basketId");
            if (isNotNullOrEmpty(tBasketId))
                tItemIds.push(tBasketId);
        }
        return (tItemIds);
    }
    getSubArticleIds(pBasketId) {
        if (this.mItemRoot == null)
            return (null);
        const tItemIds = new Array();
        const tElements = this.mItemRoot.querySelectorAll("[basketId] > mainItem[id='" + pBasketId + "']");
        for (let i = 0; i < tElements.length; ++i) {
            const tParent = tElements.item(i).parentElement;
            if (tParent != null) {
                const tBasketId = tParent.getAttribute("basketId");
                if (isNotNullOrEmpty(tBasketId))
                    tItemIds.push(tBasketId);
            }
        }
        return (tItemIds);
    }
}
